import { Avatar } from "web-core";
import { Notification } from "creso-core/api";
import {
  getInitials,
  NOTIFICATION_DESCRIPTION,
  NOTIFICATION_TITLE,
} from "creso-core/utils";
import { formatDistanceToNow } from "date-fns";
import { useMarkAsRead, useMarkAsUnread } from "creso-core/hooks";
import { handleError } from "@/utils/error";
import { cn, Tooltip, TooltipContent, TooltipTrigger } from "web-design-system";
import { Link, LinkComponentProps } from "@tanstack/react-router";
import { UnreadIcon } from "./unread-icon";
import { ReadIcon } from "./read-icon";
import {
  CLIENT_ONBOARDING_EVENTS,
  CLIENT_PAGE_EVENTS,
  RECENT_ORDER_EVENTS,
  MANDATE_EVENTS,
  STP_PLAN_STATUS_EVENTS,
  SIP_PLAN_STATUS_EVENTS,
  SWP_PLAN_STATUS_EVENTS,
} from "../config";

const StyledLink = (
  props: React.PropsWithChildren<LinkComponentProps & { onClick?: () => void }>
) => {
  return (
    <Link {...props} className={cn("no-underline!", props.className)}>
      <div
        role="link"
        onClick={props.onClick}
        className="rounded-lg no-underline! cursor-pointer hover:bg-muted group px-6 pt-4 grid grid-cols-[auto_1fr] grid-rows-[auto_auto] gap-x-3 gap-y-1"
      >
        {props.children}
      </div>
    </Link>
  );
};

export function LandingLink(
  props: React.PropsWithChildren<
    LinkComponentProps & {
      notification: Notification;
      onClick?: () => void;
    }
  >
) {
  const { notification } = props;

  const clientId = notification.details?.client?.id;
  if (!clientId) return null;

  if (CLIENT_ONBOARDING_EVENTS.includes(notification.event_type)) {
    return (
      <StyledLink
        to="/clients/$id/portfolio"
        from="/clients/$id/portfolio"
        params={{ id: clientId.toString() }}
        search={{ modal: "onboarding", client_id: clientId }}
        {...props}
      />
    );
  }

  if (CLIENT_PAGE_EVENTS.includes(notification.event_type)) {
    return (
      <StyledLink
        to="/clients/$id/portfolio"
        params={{ id: clientId.toString() }}
        {...props}
      />
    );
  }

  if (notification.event_type === "ORDER_PROCESSING_FAILED") {
    return (
      <StyledLink
        to="/clients/$id/portfolio"
        params={{ id: clientId.toString() }}
        search={{ detail: "orders", order_status: ["Failed"] }}
        {...props}
      />
    );
  }

  if (RECENT_ORDER_EVENTS.includes(notification.event_type)) {
    return (
      <StyledLink
        to="/clients/$id/portfolio"
        params={{ id: clientId.toString() }}
        search={{ detail: "orders" }}
        {...props}
      />
    );
  }

  if (notification.event_type === "MFCENTRAL_FETCH") {
    return (
      <StyledLink
        to="/clients/$id/portfolio"
        params={{ id: clientId.toString() }}
        search={{ source: "EXTERNAL" }}
        {...props}
      />
    );
  }

  if (MANDATE_EVENTS.includes(notification.event_type)) {
    return (
      <StyledLink
        to="/clients/$id/portfolio"
        from="/clients/$id/portfolio"
        params={{ id: clientId.toString() }}
        search={{ modal: "mandate", client_id: clientId }}
        {...props}
      />
    );
  }

  if (notification.event_type === "ORDER_SUBMISSION_FAILED") {
    return (
      <StyledLink
        to="/clients/$id/portfolio"
        from="/clients/$id/portfolio"
        params={{ id: clientId.toString() }}
        search={{ modal: "transaction", client_id: clientId, action: "create" }}
        {...props}
      />
    );
  }

  if (STP_PLAN_STATUS_EVENTS.includes(notification.event_type)) {
    return (
      <StyledLink
        to="/clients/$id/portfolio"
        params={{ id: clientId.toString() }}
        search={{ detail: "stp" }}
        {...props}
      />
    );
  }

  if (SIP_PLAN_STATUS_EVENTS.includes(notification.event_type)) {
    return (
      <StyledLink
        to="/clients/$id/portfolio"
        params={{ id: clientId.toString() }}
        search={{ detail: "sip" }}
        {...props}
      />
    );
  }

  if (SWP_PLAN_STATUS_EVENTS.includes(notification.event_type)) {
    return (
      <StyledLink
        to="/clients/$id/portfolio"
        params={{ id: clientId.toString() }}
        search={{ detail: "swp" }}
        {...props}
      />
    );
  }

  return null;
}

export function NotificationComponent(props: {
  notification: Notification;
  onNavigate: () => void;
}) {
  const { notification, onNavigate } = props;

  const { mutateAsync: markAsRead } = useMarkAsRead();

  const { mutateAsync: markAsUnread } = useMarkAsUnread();

  const toggleReadStatus: React.MouseEventHandler<HTMLButtonElement> = async (
    e
  ) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (notification.read_at) {
        await markAsUnread({ id: notification.id });
      } else {
        await markAsRead({ id: notification.id });
      }
    } catch (error) {
      handleError(error, "Something went wrong");
    }
  };

  const handleClick = async () => {
    markAsRead({ id: notification.id });
    onNavigate();
  };

  return (
    <LandingLink onClick={handleClick} notification={notification}>
      <Avatar
        url=""
        size="sm"
        className="size-7 text-xs"
        fallback={getInitials(notification.details?.client?.name)}
      />
      <div className="flex items-center relative">
        <span className="text-sm text-foreground/90 text-ellipsis line-clamp-1">
          {notification.details?.client?.name}
        </span>
        <Tooltip delayDuration={0} open>
          <TooltipTrigger asChild>
            <button
              className="hidden cursor-default ml-auto group-hover:flex hover:bg-muted"
              onClick={toggleReadStatus}
            >
              {notification.read_at ? (
                <ReadIcon className="stroke-primary" />
              ) : (
                <UnreadIcon className="stroke-primary" />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent className="hidden group-hover:flex" side="top">
            {notification.read_at ? "Mark as unread" : "Mark as read"}
          </TooltipContent>
        </Tooltip>
        <span className="text-muted-foreground/90 ml-auto mr-2 flex-shrink-0 group-hover:hidden text-sm">
          {formatDistanceToNow(notification.created_at, {
            addSuffix: true,
          })
            .replace("about", "")
            .replace("minutes", "mins")}
        </span>
        {!notification.read_at ? (
          <div className="size-2 bg-primary rounded-full group-hover:invisible absolute top-1/2 -translate-y-1/2 -right-2" />
        ) : null}
      </div>

      {/* Message - spans the remaining columns */}
      <div className="col-start-2 col-span-1 pb-4  text-sm border-b border-border">
        <span className="font-bold">
          {NOTIFICATION_TITLE[notification.event_type]}
        </span>{" "}
        {NOTIFICATION_DESCRIPTION[notification.event_type]?.(
          notification.details
        )}
      </div>
    </LandingLink>
  );
}
